import React, { useState } from "react";
import {
	Avatar,
	Box,
	Card,
	CardContent,
	CircularProgress,
	Collapse,
	Container,
	Divider,
	IconButton,
	Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { useMyMatchedTalents } from "../../hooks/useAnonymizedTalents";
import { AnonymizedFreelancerDetailsView } from "../../components/anonymizedTalent/AnonymizedFreelancerDetailsView";
import { AnonymizedTalent } from "../../models/AnonymizedTalent";
import { zoullooColors } from "../../theme";

/**
 * "Mes talents suggérés" — the page a connected client lands on to discover
 * the anonymized profiles the daily matcher associated to them (Option A).
 *
 * Layout mirrors the TAM `/applications` page so a client recognises the
 * pattern instantly: a single big Card containing one CardContent row per
 * talent, with an inline Collapse exposing the full anonymized detail view.
 * Avatars carry the initials (the only identity hint we ever expose at this
 * stage of the funnel).
 *
 * V1 scope keeps the experience read-only — no CTA on the client side. The
 * detail panel itself is rendered by AnonymizedFreelancerDetailsView, which
 * reuses the exact same MUI primitives as FreelancerDetails so the in-app
 * experience feels coherent across the platform.
 */
const MyMatchedTalents: React.FC = () => {
	const { t } = useTranslation();
	const { data: talents = [], isLoading, isError } = useMyMatchedTalents();
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

	const toggle = (id: number) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Box sx={{ mb: 4 }}>
				<Typography variant="h2" sx={{ color: zoullooColors.secondary.dark, mb: 1 }}>
					{t("anonymizedTalent.clientPageTitle")}
				</Typography>
				<Typography sx={{ color: zoullooColors.text.secondary }}>
					{t("anonymizedTalent.clientPageSubtitle")}
				</Typography>
			</Box>

			{isLoading && (
				<Box display="flex" justifyContent="center" sx={{ py: 8 }}>
					<CircularProgress />
				</Box>
			)}

			{isError && !isLoading && (
				<Box
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: zoullooColors.error.alpha.lighter,
						border: `1px solid ${zoullooColors.error.light}`,
						borderRadius: 2,
						color: zoullooColors.error.dark
					}}
				>
					{t("anonymizedTalent.clientLoadError")}
				</Box>
			)}

			{!isLoading && !isError && talents.length === 0 && <EmptyState />}

			{!isLoading && !isError && talents.length > 0 && (
				<Card
					sx={{
						borderBottomLeftRadius: 0,
						borderBottomRightRadius: 0
					}}
				>
					{talents.map((talent, index) => (
						<TalentRow
							key={talent.id}
							talent={talent}
							isFirst={index === 0}
							isLast={index === talents.length - 1}
							isExpanded={expandedIds.has(talent.id)}
							onToggle={() => toggle(talent.id)}
						/>
					))}
				</Card>
			)}
		</Container>
	);
};

const TalentRow: React.FC<{
	talent: AnonymizedTalent;
	isFirst: boolean;
	isLast: boolean;
	isExpanded: boolean;
	onToggle: () => void;
}> = ({ talent, isFirst, isLast, isExpanded, onToggle }) => {
	const { t } = useTranslation();

	const initials =
		`${talent.firstNameInitial ?? ""}.${talent.lastNameInitial ?? ""}.`.toUpperCase();

	const expertiseLabel = talent.expertise?.label
		? t(`Expertises.${talent.expertise.label}`, talent.expertise.label)
		: "";
	const experienceLabel = talent.experience?.label
		? t(`Experiences.${talent.experience.label}`, talent.experience.label)
		: "";

	// Closed preview headline: the profile title is too variable in quality /
	// length, so we surface expertise + experience instead — keeping the title's
	// size and weight (h3).
	const headline =
		[expertiseLabel, experienceLabel].filter(Boolean).join(" - ") ||
		t("anonymizedTalent.defaultTitle");

	return (
		<CardContent sx={{ pt: isFirst ? "32px" : "auto" }}>
			{/* Top row — same flex layout as the TAM Applications page */}
			<Box display="flex" alignItems="flex-start" gap={3}>
				{/* Initials avatar (no photo per anonymization spec) */}
				<Avatar
					sx={{
						width: 56,
						height: 56,
						fontWeight: 700,
						fontSize: "1rem",
						backgroundColor: zoullooColors.proposal.magnolia,
						color: zoullooColors.secondary.dark
					}}
				>
					{initials}
				</Avatar>

				{/* Main content — expertise + experience as the headline */}
				<Box flex={1}>
					<Typography variant="h3" component="span">
						{headline}
					</Typography>
					{talent.freelancingYearsOfExperience?.label && (
						<Box mt="1px">
							<Typography variant="body2" color="text.secondary">
								{t("freelancerDetails.freelancerSince")}{" "}
								{t(
									`FreelancingYearsOfExperiences.${talent.freelancingYearsOfExperience.label}`,
									talent.freelancingYearsOfExperience.label
								)}
							</Typography>
						</Box>
					)}
				</Box>

				{/* Right side — expand/collapse toggle (matches Applications.tsx).
				    Down arrow when collapsed (invites to open), up arrow when expanded
				    (invites to close). */}
				<Box
					display="flex"
					flexDirection="column"
					alignItems="flex-end"
					gap={4}
					position="relative"
				>
					<Box display="flex" gap={0.7}>
						<IconButton
							disableRipple
							onClick={onToggle}
							aria-label={
								isExpanded
									? t("anonymizedTalent.collapseRowAria")
									: t("anonymizedTalent.expandRowAria")
							}
							aria-expanded={isExpanded}
						>
							{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
					</Box>
				</Box>
			</Box>

			{/* Anonymized detail view rendered inline on expand */}
			<Collapse in={isExpanded} timeout={400} unmountOnExit>
				<AnonymizedFreelancerDetailsView talent={talent} />
			</Collapse>

			{!isLast && (
				<Divider sx={{ height: 16 }} orientation="horizontal" variant="middle" />
			)}
		</CardContent>
	);
};

const EmptyState: React.FC = () => {
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				p: 6,
				textAlign: "center",
				backgroundColor: zoullooColors.proposal.magnolia,
				borderRadius: 2,
				border: `1px dashed ${zoullooColors.secondary.light}`
			}}
		>
			<Typography
				sx={{
					fontSize: 18,
					fontWeight: 700,
					color: zoullooColors.secondary.dark,
					mb: 1
				}}
			>
				{t("anonymizedTalent.clientEmptyTitle")}
			</Typography>
			<Typography sx={{ fontSize: 14, color: zoullooColors.text.secondary }}>
				{t("anonymizedTalent.clientEmptySubtitle")}
			</Typography>
		</Box>
	);
};

export default MyMatchedTalents;
